import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import instance, { setAuthToken, setCsrfToken, resetSessionExpiry } from "../utils/api_instance.util";
import { loginFailureMessage } from "../utils/apiError.util";
import { errorToastify, resumeErrorToasts } from "../utils/toastify.util";

const initialState = {
	isLoading: false,
	// Cookie session is source of truth; local remember flag only reduces new-tab flicker.
	isLoggedIn:
		sessionStorage.getItem("admin_auth_active") === "true" ||
		localStorage.getItem("admin_auth_remember") === "true" ||
		Boolean(localStorage.getItem("token")),
	mfaRequired: false,
	mfaEmail: null,
	mfaMessage: null,
	mfaChallengeId: null,
	currentUser: null,
};

export const checkSession = createAsyncThunk(
	"login/checkSession",
	async () => {
		try {
			const { data } = await instance.get("/api/v1/admin/auth/me");
			if (data?.result?.csrfToken) {
				setCsrfToken(data.result.csrfToken);
			}
			return data;
		} catch (err) {
			sessionStorage.removeItem("admin_auth_active");
			localStorage.removeItem("admin_auth_remember");
			return { success: false };
		}
	}
);

export const userLoggedIn = createAsyncThunk(
	"login/userLoggedIn",
	async (payload) => {
		try {
			resumeErrorToasts();
			resetSessionExpiry();
			const { data } = await instance.post(
				"/api/v1/admin/auth/login",
				payload
			);
			if (data?.result?.csrfToken) {
				setCsrfToken(data.result.csrfToken);
			}
			return data;
		} catch (err) {
			const message = loginFailureMessage(err);
			errorToastify(message);
			return { success: false, message };
		}
	}
);

const loginSlice = createSlice({
	name: "login",
	initialState,
	reducers: {
		closealert: (state) => {
			state.isLoading = false;
			state.error = "";
		},
		resetMfa: (state) => {
			state.mfaRequired = false;
			state.mfaEmail = null;
			state.mfaMessage = null;
			state.mfaChallengeId = null;
			state.isLoading = false;
		},
		logout: (state) => {
			state.isLoggedIn = false;
			state.isLoading = false;
			state.mfaRequired = false;
			state.mfaEmail = null;
			state.mfaMessage = null;
			state.mfaChallengeId = null;
			state.currentUser = null;
			sessionStorage.removeItem("admin_auth_active");
			localStorage.removeItem("admin_auth_remember");
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			localStorage.removeItem("staff");
			localStorage.removeItem("menus");
			setAuthToken("");
		},
	},
	extraReducers: (builder) => {
		builder.addCase(userLoggedIn.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(userLoggedIn.fulfilled, (state, action) => {
			if (action.payload?.success) {
				if (action.payload.result?.mfaRequired) {
					state.isLoading = false;
					state.isLoggedIn = false;
					state.mfaRequired = true;
					state.mfaEmail = action.payload.result.email;
					state.mfaMessage = action.payload.result.message || "A 6-digit verification code has been sent to your email.";
					state.mfaChallengeId = action.payload.result.challengeId || null;
					return;
				}

				const { token, menus, user, permissions, session } = action.payload.result || {};

				state.isLoading = false;
				state.isLoggedIn = true;
				state.mfaRequired = false;
				state.mfaEmail = null;
				state.mfaMessage = null;
				state.mfaChallengeId = null;
				state.currentUser = user || null;

				sessionStorage.setItem("admin_auth_active", "true");
				if (session?.rememberMe) {
					localStorage.setItem("admin_auth_remember", "true");
				} else {
					localStorage.removeItem("admin_auth_remember");
				}

				if (menus) localStorage.setItem("menus", JSON.stringify(menus));
				if (user) {
					localStorage.setItem("staff", JSON.stringify({
						name: user.name || "",
						email: user.email || "",
						permissions: permissions || [],
					}));
				}

				if (token) {
					setAuthToken(token);
				}
			} else {
				state.isLoading = false;
				state.isLoggedIn = false;
			}
		});
		builder.addCase(userLoggedIn.rejected, (state) => {
			state.isLoading = false;
			state.isLoggedIn = false;
			sessionStorage.removeItem("admin_auth_active");
			localStorage.removeItem("admin_auth_remember");
			localStorage.removeItem("token");
		});

		builder.addCase(checkSession.fulfilled, (state, action) => {
			if (action.payload?.success && action.payload?.result?.user) {
				state.isLoggedIn = true;
				state.currentUser = action.payload.result.user;
				sessionStorage.setItem("admin_auth_active", "true");
				if (action.payload.result.menus) {
					localStorage.setItem("menus", JSON.stringify(action.payload.result.menus));
				}
				if (action.payload.result.permissions) {
					localStorage.setItem("staff", JSON.stringify({
						name: action.payload.result.user.name || "",
						email: action.payload.result.user.email || "",
						permissions: action.payload.result.permissions || [],
					}));
				}
			} else {
				state.isLoggedIn = false;
				sessionStorage.removeItem("admin_auth_active");
				localStorage.removeItem("admin_auth_remember");
			}
		});
	},
});

export default loginSlice.reducer;

export const signOut = () => async (dispatch) => {
	try {
		await instance.post("/api/v1/admin/auth/logout");
	} catch {
		// Session cleared even if network fails
	}
	dispatch(logout());
	window.location.assign("/login");
};

export const { logout, resetMfa } = loginSlice.actions;
